<template>
    <div class="relative h-[600px] w-auto">
        <div ref="container"
            class="h-full"></div>
    </div>
</template>

<script setup lang="ts">
import { whenever } from '@vueuse/core';
import loadGoogleMapsApi from 'load-google-maps-api';
import type { MapMarker } from '~~/types/components/maps';

const properties = defineProps<{
    location: string
    markers?: MapMarker[]
    filteredMarkers?: MapMarker[]
}>();

const {
    public: {
        googleApi,
    },
} = useRuntimeConfig();

const { t } = useI18n();

const container = ref<HTMLDivElement>();
const map = ref<google.maps.Map>();
const markers = ref<Record<string, google.maps.Marker>>({});

function resetLocation() {
    map.value?.setCenter({
        lat: Number.parseFloat(`${t('map.lat')}`),
        lng: Number.parseFloat(`${t('map.lng')}`),
    });

    map.value?.setZoom(Number.parseFloat(`${t('map.zoom')}`));
}

function setCenter(location: string): void {
    const [lat, lng] = location.split(',').map(Number);

    if (lat === 0 && lng === 0) {
        resetLocation();

        return;
    }

    map.value?.setCenter({
        lat,
        lng,
    });

    map.value?.setZoom(16);
}

function setMarkerVisibility(items: MapMarker[]): void {
    const visibleCoordinates = items.map((x) => x.coordinates);

    Object.entries(markers.value).forEach(([location, marker]) => {
        marker.setVisible(visibleCoordinates.includes(location));
    });

    if (items.length === 0 || items.length === Object.entries(markers.value).length) {
        resetLocation();
    } else {
        const coords = visibleCoordinates.map((coord) => coord.split(',').map(Number));

        map.value?.fitBounds(
            {
                north: Math.min(...coords.map((coord) => coord[0])),
                south: Math.max(...coords.map((coord) => coord[0])),
                west: Math.min(...coords.map((coord) => coord[1])),
                east: Math.max(...coords.map((coord) => coord[1])),
            },
        );

        const zoom = map.value?.getZoom();

        if (zoom && zoom > 16) {
            map.value?.setZoom(16);
        }
    }
}

async function loadMap(): Promise<void> {
    if (!container.value) {
        return;
    }

    const googleMaps = await loadGoogleMapsApi({
        key: googleApi,
    });

    map.value = new googleMaps.Map(container.value);

    resetLocation();

    const infoWindow = new googleMaps.InfoWindow();

    markers.value = Object.fromEntries(properties.markers?.map((markerInfo) => {
        const { coordinates, title } = markerInfo;

        const [lat, lng] = coordinates.split(',').map(Number);

        const marker = new google.maps.Marker({
            position: {
                lat,
                lng,
            },
            map: map.value,
            title,
        });
        marker.addListener('click', () => {
            infoWindow.close();
            infoWindow.setContent(marker.getTitle());
            infoWindow.open(marker.getMap(), marker);
        });

        return [coordinates, marker];
    }) || []);
}

whenever(container, async () => {
    await loadMap();

    if (properties.location) {
        setCenter(properties.location);
    }
});

watch(() => properties.location, (value) => {
    setCenter(value);
});

watch(() => properties.filteredMarkers, (value) => {
    setMarkerVisibility(value || []);
});
</script>
